/**
 * PLT-7765: Eevo promo codes. A campaign code that an account admin enters on the billing page for
 * free credit, booked like a credit grant (`./credit-grants.ts`): granted credit, spent first, not
 * cash and not revenue, and it does not expire. Upright staff create and revoke codes on the
 * internal promo codes page.
 *
 * A code limits its redemptions in total and, optionally, per email domain of the person redeeming
 * it; each account and each person can redeem a code once. People see no figures: a code that is
 * full says so, without saying how many have used it.
 */
import { isDomainToBlock } from '../iam/users/dma-email-validation';

/** Codes are matched case-insensitively and without spaces: stored and compared upper-case. */
export function normalizePromoCode(text: string): string {
  return text.replace(/\s+/g, '').toUpperCase();
}

/** What a code may look like (after `normalizePromoCode`): letters, digits, `-` and `_`. */
export const PROMO_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,39}$/;

/** The domain of an email address, lower-case; '' when it has none. */
export function emailDomainOf(email: string): string {
  const at = email.lastIndexOf('@');
  return at < 0
    ? ''
    : email
        .slice(at + 1)
        .trim()
        .toLowerCase();
}

/**
 * Who redeemed a code, for the once-per-person rule: the email lower-case and without a `+tag`, so
 * jane+1@acme.com and jane+2@acme.com count as one person.
 */
export function promoPersonKey(email: string): string {
  const lower = email.trim().toLowerCase();
  const at = lower.lastIndexOf('@');
  if (at < 0) return lower;
  return `${lower.slice(0, at).replace(/\+.*$/, '')}${lower.slice(at)}`;
}

/** The billing page's query parameter that fills in a code: `/billing?code=FIRSTFELLOW50`. */
export const PROMO_CODE_QUERY_PARAM = 'code';

/** Why a code was not redeemed, or `redeemed`. */
export type PromoRedeemOutcome =
  | 'redeemed'
  | 'invalid'
  | 'expired'
  | 'full'
  | 'domainFull'
  | 'accountAlreadyRedeemed'
  | 'personAlreadyRedeemed'
  | 'workEmailRequired'
  | 'emailNotEligible';

/** What the billing page shows for each outcome. No figures, on purpose: see the top of this file. */
export const PROMO_REDEEM_MESSAGES: Record<Exclude<PromoRedeemOutcome, 'redeemed'>, string> = {
  invalid: 'This code is not valid. Check it and try again.',
  expired: 'This campaign has ended.',
  full: 'This campaign is full.',
  domainFull:
    "Your company has reached the limit for this code. Ask a colleague to invite you to your company's Eevo organization.",
  accountAlreadyRedeemed: 'This account has already used this code.',
  personAlreadyRedeemed: 'You have already used this code.',
  workEmailRequired: 'This code is not available for your email address. Sign in with your work email.',
  emailNotEligible: 'This code is not available for your email address.',
};

/** A code and its redemptions so far, as the redeem check reads them. */
export interface PromoCodeState {
  maxRedemptions: number | null;
  maxPerEmailDomain: number | null;
  /** Lower-case domains; null for any. */
  allowedEmailDomains: string[] | null;
  startsAt: Date;
  endsAt: Date | null;
  revokedAt: Date | null;
  redemptions: number;
  /** Redemptions by people with the redeeming person's email domain. */
  domainRedemptions: number;
  accountRedeemed: boolean;
  personRedeemed: boolean;
}

/**
 * Whether the person may redeem the code for the account: null when they may, else why not. `code`
 * is null when no code matches. The order decides which reason a person sees when several apply:
 * what the person can do nothing about (an unknown, ended or used code) before the limits.
 */
export function checkPromoRedemption(
  code: PromoCodeState | null,
  email: string,
  now: Date,
): Exclude<PromoRedeemOutcome, 'redeemed'> | null {
  if (!code || code.revokedAt !== null || now < code.startsAt) return 'invalid';
  if (code.endsAt !== null && now >= code.endsAt) return 'expired';
  if (code.accountRedeemed) return 'accountAlreadyRedeemed';
  if (code.personRedeemed) return 'personAlreadyRedeemed';
  const domain = emailDomainOf(email);
  if (code.allowedEmailDomains !== null && !code.allowedEmailDomains.includes(domain)) return 'emailNotEligible';
  // A per-domain limit means nothing for gmail.com and the like: anyone can have such an address.
  if (code.maxPerEmailDomain !== null && (domain === '' || isDomainToBlock(email))) return 'workEmailRequired';
  if (code.maxRedemptions !== null && code.redemptions >= code.maxRedemptions) return 'full';
  if (code.maxPerEmailDomain !== null && code.domainRedemptions >= code.maxPerEmailDomain) return 'domainFull';
  return null;
}

/** POST /api/arvo/billing/promo-codes/redeem */
export interface ArvoPromoRedeemRequest {
  code: string;
}

export type ArvoPromoRedeemResponse =
  | { outcome: 'redeemed'; amountUsd: number; balanceUsd: number }
  | { outcome: Exclude<PromoRedeemOutcome, 'redeemed'>; message: string };

/** One code on the internal promo codes page: GET /api/admin/arvo-business/promo-codes */
export interface EevoPromoCode {
  id: number;
  code: string;
  amountUsd: number;
  maxRedemptions: number | null;
  maxPerEmailDomain: number | null;
  allowedEmailDomains: string[] | null;
  startsAt: string;
  endsAt: string | null;
  revokedAt: string | null;
  /** Internal: what the campaign is for. Never shown to the people redeeming it. */
  note: string;
  createdBy: string;
  createdAt: string;
  redemptions: number;
}

/** One redemption: GET /api/admin/arvo-business/promo-codes/{id}/redemptions */
export interface EevoPromoRedemption {
  organizationId: number;
  accountName: string;
  email: string;
  emailDomain: string;
  createdAt: string;
}

/** POST /api/admin/arvo-business/promo-codes */
export interface EevoPromoCodeCreateRequest {
  code: string;
  amountUsd: number;
  maxRedemptions: number | null;
  maxPerEmailDomain: number | null;
  allowedEmailDomains: string[] | null;
  /** ISO time; now when left out. */
  startsAt?: string;
  /** ISO time the code stops working; null for never. */
  endsAt: string | null;
  note: string;
}
